import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api-client";

export interface InboxAiDraft {
  id: string;
  text: string;
  confidence: number;
  category: string;
  status: "PENDING" | "SENT" | "AUTO_SENT" | "DISCARDED" | "SUPERSEDED";
  createdAt: string;
}

/** Send an AI draft (optionally with a human-edited text). */
export function useSendDraft(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { draftId: string; text?: string }) =>
      apiFetch<{ draft: InboxAiDraft }>(
        `/brands/${brandId}/inbox/drafts/${input.draftId}/send`,
        {
          method: "POST",
          body: JSON.stringify(input.text !== undefined ? { text: input.text } : {}),
        },
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["inbox-conversation", brandId] });
      qc.invalidateQueries({ queryKey: ["inbox-comments", brandId] });
    },
    onError: () => {
      toast.error("This draft is no longer available — it may have been superseded or already sent.");
    },
  });
}

/** Discard a PENDING AI draft. */
export function useDiscardDraft(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      apiFetch<{ draft: InboxAiDraft }>(
        `/brands/${brandId}/inbox/drafts/${draftId}/discard`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["inbox-conversation", brandId] });
      qc.invalidateQueries({ queryKey: ["inbox-comments", brandId] });
    },
    onError: () => {
      toast.error("This draft is no longer available — it may have been superseded or already sent.");
    },
  });
}
